'use client';

import LessonDetailsModal from '@/app/[domain]/[locale]/my-classes/[format]/[id]/contents/[lessonId]/components/lesson-details-modal';
import { useGetMyCourseActivities } from '@/hooks/use-activity-api';
import { useGetClassResources } from '@/hooks/use-class-resource';
import { Link } from '@/i18n/routing';
import { cn } from '@/utils';
import { CourseUtils } from '@/utils/course-utils';
import { LiveClassUtils } from '@/utils/live-class-utils';
import { TimeZoneUtils } from '@/utils/timezone-utils';
import {
  CourseContent,
  CourseFormat,
  LessonType,
  ValueOf,
} from '@hidayahlab/academy-suite-corelib';
import {
  Alert01Icon,
  BookOpen02Icon,
  CheckmarkCircle01Icon,
  Mic01Icon,
  PlayCircleIcon,
  ProfileIcon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { SpinnerIcon } from '@tajdid/tajdid-ui/icons';
import { User } from 'lucia';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';
import LiveStreamingIcon from './components/live-streaming-icon';
import ResourcesDownloadDropdown from './resource-dropdown';

type ContentProps = {
  type: CourseContent['type'];
  courseFormat: ValueOf<typeof CourseFormat>;
  label: string;
  isFree?: boolean;
  id: number;
  courseId: number;
  isEnrolled: boolean;
  contentLength?: number;
  currentUser?: User | null;
  liveClassDate?: string;
  showResource: boolean;
  showActivity: boolean;
  lessonType?: ValueOf<typeof LessonType>;
};

const typeToIconMap: Record<CourseContent['type'], ReactNode> = {
  LESSON: null,
  QUIZ: <HugeiconsIcon icon={ProfileIcon} className="text-gray-500 size-5" />,
  RESOURCE: <HugeiconsIcon icon={BookOpen02Icon} className="size-5" />,
  VOICE_QUIZ: (
    <HugeiconsIcon icon={Mic01Icon} className="text-gray-500 size-5" />
  ),
  ASSIGNMENT: <HugeiconsIcon icon={BookOpen02Icon} className="size-5" />,
};

const renderVideoIcon = (
  lessonStatus: string,
  courseFormat: ValueOf<typeof CourseFormat>,
  isClassAired: boolean,
  lessonType?: ValueOf<typeof LessonType>,
) => {
  if (lessonStatus === 'completed') {
    return (
      <HugeiconsIcon icon={PlayCircleIcon} className="text-gray-500 size-5" />
    );
  }

  if (lessonType === 'RECORDED' || courseFormat === 'RECORDED') {
    return (
      <HugeiconsIcon icon={PlayCircleIcon} className="text-gray-500 size-5" />
    );
  }

  if (isClassAired) {
    return (
      <HugeiconsIcon icon={PlayCircleIcon} className="text-gray-500 size-5" />
    );
  }

  if (
    lessonStatus === 'not_started' &&
    (lessonType === 'LIVE' || lessonType === 'SUPPORT')
  ) {
    return <LiveStreamingIcon color="#475467" />;
  }

  if (lessonStatus === 'on_going') {
    return (
      <div className="animate-pulse">
        <LiveStreamingIcon color="red" />
      </div>
    );
  }
};

const contentTypeMap = {
  LESSON: 'contents',
  QUIZ: 'quizzes',
  RESOURCE: 'resources',
  VOICE_QUIZ: 'voice-quizzes',
  ASSIGNMENT: 'assignments',
};

export default function CurriculumContent({
  type,
  label,
  isFree,
  isEnrolled,
  id,
  courseFormat,
  liveClassDate,
  courseId,
  showResource,
  showActivity,
  lessonType,
}: ContentProps) {
  const path = usePathname();
  const t = useTranslations();
  const { contentType, contentId } =
    CourseUtils.extractContentTypeAndIdFromPath(path);
  const [isMounted, setIsMounted] = useState(false);
  const [isFreeLessonModalOpen, setIsFreeLessonModalOpen] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const lessonId = type === 'LESSON' && Number(id) ? Number(id) : null;
  const isLiveOngoing = LiveClassUtils.isLiveOngoing(liveClassDate);
  const isClassAired = LiveClassUtils.isClassAired(liveClassDate);

  const lessonStatus = isLiveOngoing ? 'on_going' : 'not_started';

  const {
    data: activities,
    isLoading: isActivitiesLoading,
    isError: isActivitiesError,
  } = useGetMyCourseActivities(showActivity ? courseId : undefined);
  const { data: resources, isLoading: isResourcesLoading } =
    useGetClassResources(
      courseId,
      showResource && lessonId ? lessonId : undefined,
    );

  const activityContent = id
    ? activities?.[type] && activities?.[type][id]
    : null;

  const shouldShowLink = () => {
    if (
      courseFormat === 'LIVE' &&
      type === 'LESSON' &&
      (lessonType === 'LIVE' || lessonType === 'SUPPORT') &&
      (isLiveOngoing || !isClassAired)
    ) {
      return false;
    }

    if (lessonType === 'RECORDED' || courseFormat === 'RECORDED') {
      return isEnrolled || isFree;
    }

    return isEnrolled || isFree;
  };

  const activeContent =
    contentType === contentTypeMap[type] && id === Number(contentId);

  const renderContentDescription = () => (
    <>
      {type === 'QUIZ' && (
        <p className="mt-1 text-sm text-gray-400">{t('course-details.quiz')}</p>
      )}
      {type === 'RESOURCE' && (
        <p className="mt-1 text-sm text-gray-400">
          {t('course-details.reading')}
        </p>
      )}
      {type === 'ASSIGNMENT' && (
        <p className="mt-1 text-sm text-gray-400">
          {t('course-details.assignment')}
        </p>
      )}
      {type === 'LESSON' && courseFormat === 'LIVE' && (
        <p className="mt-1 text-sm text-gray-500">
          {isClassAired || lessonType === 'RECORDED'
            ? t('course-details.recorded')
            : `${t('course-details.live')} ${
                liveClassDate
                  ? `on ${TimeZoneUtils.detectUserTimezoneAndDisplayDate(new Date(liveClassDate), 'EEE, d MMM, h:mm a')}`
                  : ''
              }`}
        </p>
      )}
      {type === 'LESSON' && courseFormat === 'RECORDED' && (
        <p className="mt-1 text-sm text-gray-400">
          {t('course-details.video')}
        </p>
      )}

      {type === 'VOICE_QUIZ' && (
        <p className="mt-1 text-sm text-gray-400">
          {t('course-details.voice-quiz')}
        </p>
      )}
    </>
  );

  const renderStatusIcon = () => {
    if (!isMounted) return null;

    if (isActivitiesLoading || isResourcesLoading) {
      return <SpinnerIcon className="text-gray-500 size-5" />;
    }

    if (isActivitiesError) {
      return (
        <HugeiconsIcon icon={Alert01Icon} className="text-error-500 size-5" />
      );
    }

    if (
      showActivity &&
      activityContent &&
      activityContent.status === 'COMPLETED'
    ) {
      return (
        <HugeiconsIcon
          icon={CheckmarkCircle01Icon}
          className="text-success-500 size-5"
        />
      );
    }
  };

  return (
    <div className="flex gap-1 items-start md:gap-3">
      <div>
        <div className="flex z-20 justify-center items-center text-gray-500">
          <div className="p-1 rounded-full">
            {type === 'LESSON' && courseFormat
              ? renderVideoIcon(
                  lessonStatus,
                  courseFormat,
                  isClassAired,
                  lessonType,
                )
              : typeToIconMap[type]}
          </div>
        </div>
      </div>

      <div className="flex items-start justify-between w-full gap-3 lg:flex-row mt-[1px] lg:-mt-[2px]">
        <div>
          {shouldShowLink() ? (
            <Link
              href={CourseUtils.curriculumContentTypeToLinkMap[type](
                id,
                courseId,
                courseFormat,
              )}
              className={cn(
                'text-base hover:underline font-normal text-gray-600 flex items-center gap-2 flex-wrap',
                {
                  'font-medium cursor-pointer': isEnrolled || isFree,
                  'text-primary-500 underline': activeContent,
                },
              )}
            >
              {lessonType && lessonType === 'SUPPORT' && (
                <span className="text-gray-400">
                  {t('common.support-class')}
                </span>
              )}
              {type === 'QUIZ' ? 'QUIZ' : label}
            </Link>
          ) : (
            <div className="text-sm text-gray-600 lg:text-base flex items-center gap-2 flex-wrap">
              {lessonType && lessonType === 'SUPPORT' && (
                <span className="text-gray-400">
                  {t('common.support-class')}:
                </span>
              )}
              {type === 'QUIZ' ? 'QUIZ' : label}
            </div>
          )}
          {renderContentDescription()}
        </div>

        <div className="flex gap-2 items-center">
          {showResource &&
            isEnrolled &&
            resources &&
            resources?.length > 0 &&
            lessonId && (
              <ResourcesDownloadDropdown
                courseId={courseId}
                lessonId={lessonId}
                resources={resources}
              />
            )}
          <div> {renderStatusIcon()}</div>
          {!isEnrolled && isFree && (
            <div className="p-1 text-white lg:px-3 rounded-xs bg-primary-500">
              <p className="text-[10px] lg:text-xs">Free</p>
            </div>
          )}
        </div>
      </div>
      {isFreeLessonModalOpen && lessonId && (
        <LessonDetailsModal
          isOpen={isFreeLessonModalOpen}
          courseId={courseId}
          lessonId={lessonId}
          setIsFreeLessonModalOpen={setIsFreeLessonModalOpen}
        />
      )}
    </div>
  );
}
